import {inject, Injectable, SecurityContext} from "@angular/core";
import {AbstractService} from "../abstract.service";
import {MediaRepo} from "../../repositories/api/media.repo";
import {map, switchMap} from "rxjs/operators";
import {DomSanitizer, SafeUrl} from "@angular/platform-browser";
import {Observable} from "rxjs";
import {HttpResponse} from "@angular/common/http";
import {PDFDocument} from 'pdf-lib';
import {DataUrl} from "../../base/data/data-url";
import {isDefined, notDefined} from "../../libs/utils/epic.utils";
import {MediaType} from "../../libs/media/media.type";
import {DeletionProtection} from "../../libs/enum/deletion-protection.enum";
import {Belong} from "../../libs/enum/belong.enum";
import {MediaDto} from "../../repositories/dto/media.dto";
import * as UTIF from 'utif';


@Injectable({providedIn: 'root'})
export class MediaService extends AbstractService {

  private static readonly MIME_TYPES_TIFF: string[] = [MediaType.TIF, MediaType.TIFF];
  private static readonly A4_PAGE_WIDTH: number = 595;  // A4 in points (72 dpi)
  private static readonly A4_PAGE_HEIGHT: number = 842; // A4 in points (72 dpi)

  private readonly sanitizer: DomSanitizer = inject(DomSanitizer);
  private readonly mediaRepo: MediaRepo = this.repoProvider.getRepo(MediaRepo);

  public loadImageContent(mediaId: string): Observable<SafeUrl> {
    return this.loadContent(mediaId)
      .pipe(map((res) => this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(res.body!))));
  }

  public loadContent(mediaId: string): Observable<HttpResponse<Blob>> {
    return this.mediaRepo.loadContent(this.getOrg(), mediaId);
  }

  public loadContentForView(mediaId: string): Observable<SafeUrl> {
    return this.loadContent(mediaId).pipe(switchMap((res) => new Observable<SafeUrl>(obs => {
        const reader = new FileReader();
        reader.readAsDataURL(res.body!);
        reader.onload = (data) => {
          const dataUrl = DataUrl.parse(String(data.target!.result));
          this.prepareForView(dataUrl).then((result) => {
            const url = typeof result === 'string' ? result : result.dataUrl;
            obs.next(this.sanitizer.bypassSecurityTrustResourceUrl(url));
            obs.complete();
          });
        };
        reader.onerror = () => {
          obs.error(reader.error);
          obs.complete();
        };
        // Cleanup if observable is unsubscribed
        return () => reader.abort();
      })
    ));
  }

  public fileUpload(parentId: string, parentType: Belong, tags: string[], deletionProtection: DeletionProtection, attachment: boolean, file: File): Observable<MediaDto> {
    return this.mediaRepo.fileUpload(this.getOrg(), parentId, parentType, tags.join(','), deletionProtection, attachment, file)
      .pipe(map((w) => w.getFirstData()));
  }

  public formatXml(mediaUrl: SafeUrl): Promise<string> {
    return new Promise((resolve) => {
      const url = this.sanitizer.sanitize(SecurityContext.URL, mediaUrl);
      if (url) {
        fetch(url)
          .then((res) => res.text())
          .then((xml) => {
            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(xml, 'text/xml');
            const serializer = new XMLSerializer();
            resolve(this.beautifyXml(serializer.serializeToString(xmlDoc)));
          })
          .catch(() => resolve(this.i18n.translate('media.view.error')));
      }
    });
  }

  public formatCsv(mediaUrl: SafeUrl): Promise<string> {
    return new Promise((resolve) => {
      const url = this.sanitizer.sanitize(SecurityContext.URL, mediaUrl);
      if (url) {
        fetch(url)
          .then((res) => res.text())
          .then((csv) => resolve(csv))
          .catch(() => resolve(this.i18n.translate('media.view.error')));
      }
    });
  }

  // Example: inline; filename="2024-08-07_Lieferschein_L-000031.pdf"
  public parseContentDisposition(header: string | null, contentType: string | undefined): string {
    if (isDefined(header)) {
      // filename*= zuerst: Der einfache Parameter traegt nur ASCII, weil Node keine anderen Zeichen in
      // eine Kopfzeile laesst - siehe buildContentDisposition() in electron/main.js. Ein georgischer
      // oder kyrillischer Dateiname steht ausschliesslich hier.
      const extended = header.match(/filename\*=UTF-8''([^;]+)/i);
      if (isDefined(extended)) {
        return decodeURIComponent(extended[1]);
      }
      const match = header.match(/filename="(.+)"/);
      if (isDefined(match)) {
        return decodeURIComponent(match[1]);
      }
    }
    // Fallback - filename not found
    switch (contentType) {
      default:
      case MediaType.PDF:
        return 'download.pdf';
      case MediaType.PNG:
        return 'download.png';
      case MediaType.XML:
        return 'download.xml';
    }
  }

  /*
   * Simple formatting with line breaks.
   * You can use a formatter lib at this point if you need better formatting.
   */
  private beautifyXml(xml: string): string {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xml, 'text/xml');
    const serializer = new XMLSerializer();
    const xmlString = serializer.serializeToString(xmlDoc);
    return xmlString.replace(/(>)(<)(\/*)/g, '$1\n$2$3');
  }

  private prepareForView(data: DataUrl): Promise<DataUrl | string> {
    return new Promise((resolve) => {
      if (MediaService.MIME_TYPES_TIFF.includes(data.mimeType)) {
        const buffer = this.base64ToArrayBuffer(data);
        const pages = this.parseTiffToPng(buffer);
        return resolve(this.createPdfFromPng(pages));
      }
      return resolve(data);
    });
  }

  private base64ToArrayBuffer(data: DataUrl): ArrayBuffer {
    const binaryString = atob(data.base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }

  /**
   * Wandelt ein TIFF seitenweise in PNG-Bilddaten.
   *
   * 🔑 <b>Warum UTIF und nicht tiff.js.</b> Der Vorgaenger war ein asm.js-Bau von libtiff (1,5 MB) und rief intern zweimal `eval()` auf —
   * unvereinbar mit `script-src 'self'` ohne `'unsafe-eval'` (siehe `src/_headers`). `'unsafe-eval'` waere die falsche Antwort gewesen: Die
   * Richtlinie waere damit gegen genau den Angriff wirkungslos, dessentwegen es sie gibt. UTIF ist reines JavaScript (58 KB, kein `eval`) und
   * beherrscht die Kompressionen, die Scanner erzeugen — <b>Fax G3 und G4</b>, LZW, PackBits, JPEG.
   *
   * ⚠️ <b>Nicht jeder Verzeichniseintrag ist eine Seite.</b> {@link UTIF.decode} liefert auch Untereintraege — Vorschaubilder, EXIF-Bloecke.
   * Erst {@link UTIF.decodeImage} setzt Breite und Hoehe; was danach keine hat, ist keine Seite und wird uebersprungen. Ohne diese Bedingung
   * entstuenden leere Seiten im erzeugten PDF.
   */
  private parseTiffToPng(data: ArrayBuffer): DataUrl[] {
    const pages: DataUrl[] = [];
    const ifds = UTIF.decode(data);
    for (const ifd of ifds) {
      UTIF.decodeImage(data, ifd, ifds);
      if (notDefined(ifd.width) || notDefined(ifd.height)) {
        continue;
      }
      pages.push(DataUrl.parse(this.toCanvas(ifd).toDataURL()));
    }
    return pages;
  }

  /** Legt die entpackten Bildpunkte einer Seite in ein Canvas — der Weg zu `toDataURL()`, den `createPdfFromPng` erwartet. */
  private toCanvas(ifd: UTIF.Ifd): HTMLCanvasElement {
    const width = ifd.width as number;
    const height = ifd.height as number;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (notDefined(context)) {
      throw new Error('Canvas 2D context is not available');
    }
    const image = context.createImageData(width, height);
    image.data.set(UTIF.toRGBA8(ifd));
    context.putImageData(image, 0, 0);
    return canvas;
  }

  private async createPdfFromPng(datas: DataUrl[]): Promise<string> {
    const pdfDoc = await PDFDocument.create();
    const pagePromises = datas.map(async (data) => {
      const image = await pdfDoc.embedPng(data.dataUrl);
      const scale = Math.min(MediaService.A4_PAGE_WIDTH / image.width, MediaService.A4_PAGE_HEIGHT / image.height);
      const scaledWidth = image.width * scale;
      const scaledHeight = image.height * scale;
      pdfDoc.addPage([MediaService.A4_PAGE_WIDTH, MediaService.A4_PAGE_HEIGHT])
        .drawImage(image, {
          x: (MediaService.A4_PAGE_WIDTH - scaledWidth) / 2,
          y: (MediaService.A4_PAGE_HEIGHT - scaledHeight) / 2,
          width: scaledWidth,
          height: scaledHeight,
        });
    });
    await Promise.all(pagePromises);

    // data: wie jede andere Vorschau, kein blob: - Safari uebertraegt frame-ancestors 'none' auf ein Blob-Dokument und zeigt es nicht.
    return pdfDoc.saveAsBase64({dataUri: true});
  }
}
